'use client';
import React from 'react';
import { getCurrentLang, LANG_KEY } from '@/utils/i18n';
import useSettingsStorage from '@/hooks/useSettingsStorage';
import auth from '@/utils/auth';
import { Drawer, Box } from '@mantine/core';
import LoadingScreen from '@/components/ui/LoadingScreen';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import modalsMap from '@/modals/modalsMap';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import Breadcrumbs from '../Breadcrumbs';
import RequireAuth from './RequireAuth';
import CookieBanner from '../ui/CookieBanner';
import { ScrollButtonProvider } from '@/contexts/ScrollButtonContext';
import { OfflineSyncProvider } from '@/contexts/OfflineSyncContext';
import { usePathname, useRouter } from 'next/navigation';
import { setSpainRegion, ensureHolidaysLoaded } from '@/utils/holidays';
// import { useTranslation } from 'react-i18next';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

// Lazy load scroll button to reduce initial bundle
const ScrollToTopButton = React.lazy(() => import('../ScrollToTopButton'));

export function MainLayout({
  children,
  initialServerAuth,
}: {
  children: React.ReactNode;
  initialServerAuth?: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();
  // Keep translation ready for other UI; not needed in redirect screen now.
  // Synchronously check auth from localStorage so we can avoid flashing the
  // application UI before redirecting unauthenticated users to the login page.
  // `auth.isAuthenticated()` reads localStorage synchronously and is safe to
  // call during render on the client.
  const isClient = typeof window !== 'undefined';

  // Maintain an auth state that is initialized from server-provided
  // `initialServerAuth`. We avoid reading localStorage synchronously during
  // the initial client render to prevent hydration mismatches when the
  // server and client disagree. Once the client hydrates we check the
  // real auth state and update.
  const [isAuthed, setIsAuthed] = React.useState<boolean>(() => !!initialServerAuth);
  // Track whether the client-side auth check has completed. On the server
  // this is considered "checked" because we relied on `initialServerAuth`.
  const [authChecked, setAuthChecked] = React.useState<boolean>(() => !isClient);

  React.useEffect(() => {
    if (!isClient) {
      return;
    }
    let mounted = true;
    try {
      const ok = auth.isAuthenticated();
      if (mounted) {
        setIsAuthed(ok);
        setAuthChecked(true);
      }
    } catch (e) {
      if (mounted) {
        setIsAuthed(false);
        setAuthChecked(true);
      }
    }
    return () => {
      mounted = false;
    };
  }, []);
  // previously: collapsed state for icons-only sidebar (removed)
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // desktop-visible state for the permanent sidebar. The header button will
  // toggle this on larger viewports, while on small viewports it will toggle
  // the mobile drawer instead.
  // Initialize to true to match the server-rendered HTML. Read stored
  // preference in a client-only effect to avoid hydration mismatches.
  const [showSidebar, setShowSidebar] = React.useState<boolean>(() => {
    // Initialize from localStorage synchronously to avoid flash
    if (typeof window !== 'undefined') {
      try {
        const v = window.localStorage.getItem('wsc.showSidebar');
        return v === null || v === '1';
      } catch (e) {
        return true;
      }
    }
    return true;
  });
  const { load } = useSettingsStorage();

  // Check initial setup requirements asynchronously without blocking render
  React.useEffect(() => {
    if (!isClient || !authChecked || !isAuthed) {
      return;
    }

    let mounted = true;
    (async () => {
      try {
        // Check stored language and saved settings.defaultRegion
        const langStored = window.localStorage.getItem(LANG_KEY);
        const settings = await load();

        if (!mounted) {
          return;
        }

        const hasLang = !!(langStored || getCurrentLang());
        const hasRegion = typeof settings?.defaultRegion !== 'undefined';

        // Only open initial-setup modal if missing required settings
        if (!hasLang || !hasRegion) {
          // Lazy load modals API only when needed
          const { modals } = await import('@mantine/modals');
          if (mounted) {
            modals.openContextModal({ modal: 'initial-setup', innerProps: {} });
          }
        }

        // Preload holidays for the user's default region to ensure they are available
        // for payment calculations and other UI features (non-blocking)
        if (settings?.defaultRegion) {
          setSpainRegion(settings.defaultRegion);
          await ensureHolidaysLoaded(settings.defaultRegion);
        } else {
          // Load national holidays even if no region is set
          await ensureHolidaysLoaded();
        }
      } catch (e) {
        // ignore setup check failures and holiday loading errors
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isClient, authChecked, isAuthed, load]);

  // If the user appears unauthenticated and is not on /login, render a
  // minimal placeholder while the client-side effect redirects to the
  // login page. We intentionally perform the same check on the server as on
  // the client (using `isAuthedSync` which is false on the server) so the
  // server-rendered HTML matches the initial client render and avoids
  // hydration mismatches.
  React.useEffect(() => {
    if (!isClient) {
      return;
    }
    // Wait until the client-side auth check completes before redirecting.
    if (!authChecked) {
      return;
    }
    if (!isAuthed && pathname && !pathname.startsWith('/login')) {
      // Use replace so tests and back-button behavior remain sane.
      try {
        router.replace('/login');
      } catch (e) {
        // ignore navigation errors
      }
    }
  }, [isAuthed, authChecked, pathname, router, isClient]);

  // Keep a CSS variable updated with the footer height so the main area can
  // reserve exactly the right amount of bottom padding. This avoids layout
  // thrash when footer content wraps to multiple lines. Only run on client.
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const root = document.querySelector('.app-root') as HTMLElement | null;
    const footer = document.querySelector('.app-footer') as HTMLElement | null;
    if (!root || !footer) {
      return;
    }

    const setVar = (h: number) => root.style.setProperty('--footer-current-height', `${h}px`);

    // initialize
    setVar(footer.offsetHeight);

    // observe size changes
    const ro = new ResizeObserver(() => setVar(footer.offsetHeight));
    ro.observe(footer);

    // also watch for mutations that may change footer content (links added/removed)
    const mo = new MutationObserver(() => setVar(footer.offsetHeight));
    mo.observe(footer, { childList: true, subtree: true, characterData: true });

    // on window resize, recalc
    const onResize = () => setVar(footer.offsetHeight);
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handleToggleMenu = () => {
    // If we're on a wide viewport, toggle the permanent sidebar. On mobile,
    // toggle the Drawer.
    if (typeof window !== 'undefined' && window.innerWidth >= 950) {
      setShowSidebar((s) => {
        const next = !s;
        try {
          window.localStorage.setItem('wsc.showSidebar', next ? '1' : '0');
        } catch (e) {
          // ignore storage errors
        }
        return next;
      });
    } else {
      setMobileOpen((s) => !s);
    }
  };

  // If the client is unauthenticated and not on /login, render a minimal
  // placeholder while the effect (above) redirects to the login page. This
  // avoids rendering the app chrome or any content that might briefly flash
  // before navigation.
  if (!isAuthed && pathname && !pathname.startsWith('/login')) {
    // Important: do NOT wrap with .app-root grid here; it constrains children to the header grid row
    // and prevents proper centering. Render a standalone full-viewport loading screen instead.
    return (
      <LoadingScreen
        labelKey="auth.redirecting"
        defaultLabel="Redirigiendo al inicio de sesión…"
        loaderType="bars"
      />
    );
  }

  const content = (
    <>
      <div className={`app-root ${!showSidebar ? 'sidemenu-hidden' : ''}`}>
        {/* header button toggles drawer on mobile, and hides/shows sidebar on desktop */}
        {/* hide header and sidebar on login page for a focused experience */}
        {!pathname?.startsWith('/login') && (
          <>
            <Header
              onOpenMobileMenu={handleToggleMenu}
              sidebarVisible={showSidebar}
              initialServerAuth={initialServerAuth}
            />

            {showSidebar && <Sidebar />}
          </>
        )}

        {/* Mobile drawer for sidebar on small viewports */}
        <Drawer
          opened={mobileOpen}
          onClose={() => setMobileOpen(false)}
          padding="md"
          size="280px"
          /* stronger, slightly blurred overlay for better visual separation on mobile */
          overlayProps={{ opacity: 0.6, blur: 6 }}
          /* allow quick dismissal with outside taps and ESC - feels natural on mobile */
          closeOnClickOutside
          closeOnEscape
          /* remove default close button to keep UI minimal (swipe/click outside / ESC will close)
           keep control inside Sidebar via onNavigate to close after selecting an item */
          withCloseButton={false}
        >
          <Sidebar onNavigate={() => setMobileOpen(false)} forceExpanded />
        </Drawer>

        <Box component="main" className="app-main" pos="relative">
          <div className="container">
            <Breadcrumbs />
            <RequireAuth initialServerAuth={initialServerAuth}>{children}</RequireAuth>
          </div>
          {/* place the ScrollToTop button inside the .app-main host so it can
            correctly detect the scrolling container and avoid footer/overflow
            mis-detection */}
          <React.Suspense fallback={null}>
            <ScrollToTopButton />
          </React.Suspense>
        </Box>

        <Footer />
        <CookieBanner />
      </div>
    </>
  );

  return (
    <ScrollButtonProvider>
      <OfflineSyncProvider>
        <ModalsProvider modals={modalsMap} modalProps={{ size: 'xl' }}>
          <Notifications />
          {content}
        </ModalsProvider>
      </OfflineSyncProvider>
    </ScrollButtonProvider>
  );
}
